import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '../common/Table';
import Button from '../common/Button';
import Card from '../common/Card';
import api from '../../services/api';
import { formatDate } from '../../utils/formatters';
import { toast } from 'react-hot-toast';

const StaffList = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const response = await api.get('/staff.php');
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error('Failed to load staff:', error);
      toast.error('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    
    try {
      await api.delete(`/staff.php/${id}`);
      toast.success('Staff member deleted successfully');
      loadStaff();
    } catch (error) {
      console.error('Failed to delete staff:', error);
      toast.error(error.response?.data?.error || 'Failed to delete staff');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (_, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%', 
            backgroundColor: 'var(--primary)', color: 'white', 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: '600'
          }}>
            {row.first_name?.[0]}{row.last_name?.[0]}
          </div>
          <div>
            <div style={{ fontWeight: '500' }}>{row.first_name} {row.last_name}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>{row.username}</div>
          </div>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email'
    },
    {
      key: 'role_name',
      label: 'Role'
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.85rem',
          backgroundColor: value ? 'var(--success-light)' : 'var(--danger-light)',
          color: value ? 'var(--success)' : 'var(--danger)'
        }}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Joined',
      render: (value) => formatDate(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
            onClick={() => navigate(`/staff/${row.id}`)}
            title="Edit"
          >
            ✏️
          </button>
          <button 
            style={{ padding: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
            onClick={() => handleDelete(row.id)}
            title="Delete"
          >
            🗑️
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="staff-list">
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Staff Management</h2>
          <Button 
            variant="primary"
            onClick={() => navigate('/staff/new')}
            icon="➕"
          >
            Add Staff
          </Button>
        </div>

        <Table
          columns={columns}
          data={staff}
          loading={loading}
          onRowClick={(row) => navigate(`/staff/${row.id}`)}
        />
      </Card>
    </div>
  );
};

export default StaffList;
